import { XMarkIcon } from "@heroicons/react/24/outline";
import { DateTime } from "luxon";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import useDismantlingBaySetting from "../../data/useDismantlingBaySetting";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import Modal from "../../layouts/Modal";
import {
  IDismantlingBay,
  IUpsertDismantlingBaySettingRequest,
} from "../../models/DismantlingBay";
import classNames from "../../utils/classNames";
import { NumberInput } from "../shared/Inputs/NumberInput";
import LoadingPulse from "../shared/LoadingPulse";

interface BaySettingsModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  bay: IDismantlingBay;
  date: string;
}

type FormValues = {
  capacity: number;
  easy: number;
  medium: number;
  hard: number;
};

export default function BaySettingsModal({ open, setOpen, bay, date }: BaySettingsModalProps) {
  const { t } = useTranslation("production");
  const { saveHandlers } = useDefaultCRUDHandlers(t("Bay capacity"));
  const { setting, upsertSetting } = useDismantlingBaySetting({
    dismantlingBayId: open ? bay.id : undefined,
    date: open ? date : undefined,
  });

  const { register, handleSubmit, reset } = useForm<FormValues>();

  const effective = setting.data?.data;

  useEffect(() => {
    if (!open) return;

    if (effective) {
      reset({
        capacity: effective.capacity,
        easy: effective.easy,
        medium: effective.medium,
        hard: effective.hard,
      });
    } else {
      reset({
        capacity: bay.capacity,
        easy: bay.easy,
        medium: bay.medium,
        hard: bay.hard,
      });
    }
  }, [open, effective, bay, reset]);

  const onSubmit = (data: FormValues) => {
    const body: IUpsertDismantlingBaySettingRequest = {
      capacity: Number(data.capacity),
      easy: Number(data.easy),
      medium: Number(data.medium),
      hard: Number(data.hard),
    };

    upsertSetting.mutate(
      { dismantlingBayId: bay.id, date, body },
      {
        onSuccess: () => {
          saveHandlers.onSuccess();
          setOpen(false);
        },
        onError: saveHandlers.onError,
      }
    );
  };

  const formattedDate = DateTime.fromISO(date)
    .setLocale("en-gb")
    .toLocaleString(DateTime.DATE_HUGE);

  return (
    <Modal width="max-w-md" open={open} setOpen={setOpen}>
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">{t("{{name}} Capacity", { name: bay.name })}</h1>
          <p className="text-sm text-gray-500">{formattedDate}</p>
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-gray-400 hover:text-gray-600"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      {setting.isLoading ? (
        <div className="mt-4">
          <LoadingPulse />
        </div>
      ) : (
        <form className="mt-4 space-y-4" onSubmit={handleSubmit(onSubmit)}>
          <div className="grid grid-cols-2 gap-x-4 gap-y-4">
            <NumberInput
              label={t("Total vehicles")}
              registerName="capacity"
              register={register}
              min={0}
              step="1"
            />
            <NumberInput
              label={t("Easy")}
              registerName="easy"
              register={register}
              min={0}
              step="1"
            />
            <NumberInput
              label={t("Medium")}
              registerName="medium"
              register={register}
              min={0}
              step="1"
            />
            <NumberInput
              label={t("Hard")}
              registerName="hard"
              register={register}
              min={0}
              step="1"
            />
          </div>

          <p className="text-xs text-gray-500">
            {t(
              "Total caps the number of vehicles on this lane. Easy / Medium / Hard cap each complexity and are used by Auto Allocate. Unranked vehicles count only towards the total."
            )}
          </p>

          <div className="flex items-center justify-end gap-x-2 pt-2">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-sm px-3 py-1.5 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
            >
              {t("Cancel")}
            </button>
            <button
              type="submit"
              disabled={upsertSetting.isLoading}
              className={classNames(
                "text-sm px-3 py-1.5 rounded-md bg-primary-600 text-white hover:bg-primary-700",
                upsertSetting.isLoading ? "opacity-60 cursor-not-allowed" : ""
              )}
            >
              {upsertSetting.isLoading ? t("Saving...") : t("Save")}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
